import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogPost } from '../lib/microcms';

const activateScripts = (container) => {
  if (!container) return;
  container.querySelectorAll('script').forEach((old) => {
    const s = document.createElement('script');
    Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value));
    s.textContent = old.textContent;
    old.parentNode.replaceChild(s, old);
  });
};

const CATEGORY_LABELS = {
  drip: 'ドリップ',
  beans: '豆の知識',
  gear: '器具・道具',
  recipe: 'レシピ',
  owner: '店主の話',
};

const AuthorIntro = () => (
  <div className="gc-author-intro">
    <img src="/images/author-guri.png" alt="ぐり" className="gc-author-intro__icon" />
    <div className="gc-author-intro__body">
      <span className="gc-author-intro__name">ぐり</span>
      <p className="gc-author-intro__desc">
        石川県加賀エリアの焙煎士。製造業の職人として働きながら、GURI COFFEEを営むひとり会社の代表。
        2児の父で少年野球コーチ。コーヒーと人との出会いを大切にしながら、日々の暮らしや気づきを発信しています。
      </p>
      <div className="gc-author-intro__links">
        <a href="https://note.com/guri4945" target="_blank" rel="noopener noreferrer">note</a>
        <a href="https://substack.com/@guri4945" target="_blank" rel="noopener noreferrer">Substack</a>
      </div>
    </div>
  </div>
);

const AuthorBox = () => (
  <div className="gc-author">
    <div className="gc-author__inner">
      <div className="gc-author__info">
        <img src="/images/author-guri.png" alt="ぐり" className="gc-author__icon" />
        <div>
          <span className="gc-author__label">この記事を書いた人</span>
          <span className="gc-author__name">ぐり</span>
        </div>
      </div>
      <p className="gc-author__desc">
        石川県加賀エリアの焙煎士。製造業の職人として働きながら、GURI COFFEEを営むひとり会社の代表。
        コーヒーと人との向き合い方について、noteで「人と言葉を紡ぐ」を連載中。
      </p>
      <div className="gc-author__links">
        <a href="https://note.com/guri4945" target="_blank" rel="noopener noreferrer" className="gc-author__link">
          <span className="gc-author__link-platform">note</span>
          <span className="gc-author__link-desc">人との向き合い方について書いています</span>
        </a>
        <a href="https://substack.com/@guri4945" target="_blank" rel="noopener noreferrer" className="gc-author__link">
          <span className="gc-author__link-platform">Substack</span>
          <span className="gc-author__link-desc">コーヒーと日常の記録</span>
        </a>
      </div>
    </div>
  </div>
);

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const introRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getBlogPost(slug)
      .then((data) => {
        setPost(data);
        if (data) document.title = `${data.title} | GURI COFFEE`;
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  const { introHtml, bodyHtml } = useMemo(() => {
    if (!post) return { introHtml: '', bodyHtml: '' };
    const content = post.body || post.content;
    const firstH2 = content.search(/<h2[\s>]/);
    if (firstH2 === -1) {
      return { introHtml: content, bodyHtml: '' };
    }
    return {
      introHtml: content.slice(0, firstH2),
      bodyHtml: content.slice(firstH2),
    };
  }, [post]);

  useEffect(() => {
    activateScripts(introRef.current);
    activateScripts(bodyRef.current);
  }, [introHtml, bodyHtml]);

  if (loading) {
    return (
      <div className="gc-page">
        <div className="gc-page__container">
          <p className="gc-page__loading">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="gc-page">
        <div className="gc-page__container">
          <h1 className="gc-page__title">記事が見つかりません</h1>
          <Link to="/blog" className="gc-page__back">ブログ一覧へ戻る</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="gc-blogpost">
      <div className="gc-blogpost__container">
        {post.category && post.category.length > 0 && (
          <span className="gc-blogpost__cat">
            {CATEGORY_LABELS[post.category] || post.category}
          </span>
        )}
        <h1 className="gc-blogpost__title">{post.title}</h1>
        <time className="gc-blogpost__date">
          {new Date(post.publishedAt).toLocaleDateString('ja-JP')}
        </time>

        {post.eyecatch && (
          <img
            src={`${post.eyecatch.url}?w=960`}
            alt={post.title}
            className="gc-blogpost__hero"
          />
        )}

        {introHtml && (
          <div
            ref={introRef}
            className="gc-page__body"
            dangerouslySetInnerHTML={{ __html: introHtml }}
          />
        )}

        <AuthorIntro />

        {bodyHtml && (
          <div
            ref={bodyRef}
            className="gc-page__body"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        )}

        <AuthorBox />

        <Link to="/blog" className="gc-page__back">ブログ一覧へ戻る</Link>
      </div>
    </div>
  );
};

export default BlogPost;
